import React from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';

import LoadingProgress from 'lib/react-router/loading-progress';
import FetchData, { Props as FetchDataProps, Fetch, Render, Transform } from 'lib/fetch-data/fetch-data';
import ForceUpdate from 'lib/fetch-data/force-update';
import { RouteComponentProps } from 'react-router';

import { Site } from 'four-nets/site';
import { UserStore } from 'four-nets/user/user';
import { FourNetsApi } from 'four-nets/api/api';
import { RedirectError } from 'four-nets/router/errors';
import ErrorPage from 'four-nets/errors/error-page';
import { SearchApi } from 'four-nets/search/search-api';

import ProductNegotiationView from 'four-nets/bazaar/product/negotiation/product-negotiation-view';
import { CategoryStore, SuperCategoryStore, ReviewStore, Product, Buyer, Category } from 'four-nets/bazaar/bazaar';
import ProductView from 'four-nets/bazaar/product/product-view';
import { RuleStore } from 'four-nets/admin/admin';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';

interface Params {
    categorySlug: string;
    differentiator: string;
    slug: string;
}

interface Search {
    username?: string;
    token?: string;
}

export interface Props extends RouteComponentProps<Params> {
    search: Search;
}

interface Data {
    category: Category;
    product: Product;
    buyer: Buyer;
    token?: string;

    userStore: UserStore;
    reviewStore: ReviewStore;
    superCategoryStore: SuperCategoryStore;
    forceUpdate: () => void;
    searchApi: SearchApi;
}

interface FetchProps extends Search, Params, FetchDataProps<Data, Data> {
    forceUpdate: () => void;
}

class ProductLoader extends React.PureComponent<Props> {
    static contextTypes = {
        api: PropTypes.instanceOf(FourNetsApi),
        userStore: PropTypes.instanceOf(UserStore),
        site: PropTypes.instanceOf(Site),
    };

    isAuthorized = (product: Product, userStore: UserStore) => {
        const isAuthor = this.isAuthor(product, userStore);
        const isSuperstaff = this.isSuperstaff(userStore);
        return isAuthor || isSuperstaff;
    };

    isAuthor = (product: Product, userStore: UserStore) => {
        const user = userStore && userStore.me;
        return user && product && user === product.user;
    };

    isSuperstaff = (userStore: UserStore) => {
        return userStore.me && userStore.me.isSuperstaff;
    };

    loadBuyer = (product: Product, userStore: UserStore, username?: string, token?: string) => {
        let promise: Promise<void> | Promise<Buyer> = Promise.resolve();

        const isAuthorized = this.isAuthorized(product, userStore);
        const loggedInNotSeller = userStore.me && !isAuthorized;
        const sellerOrSuperstaff = username && isAuthorized;

        if (loggedInNotSeller) {
            promise = product.buyerStore.loadUsername(product, userStore.me.username);
        } else if (sellerOrSuperstaff) {
            promise = product.loadBuyer(username!);
        } else if (token) {
            promise = product.buyerStore.loadToken(product, token);
        }

        return promise as Promise<Buyer | undefined>;
    };

    fetch: Fetch<Data, FetchProps> = (setData, props) => {
        const fetchData = this.createFetchData(props);
        const { categoryStore, userStore, categorySlug, differentiator, slug, username, token } = fetchData;

        const categoryPromise = categoryStore.loadSlug({ slug: categorySlug });
        return categoryPromise.then((category: Category) => {
            return category.productStore
                .loadSlug({ slug: `${categorySlug}/${differentiator}/${slug}` })
                .then((product: Product) => {
                    if (category.slug !== categorySlug) {
                        return Promise.reject(new RedirectError(product.href, 301));
                    }

                    return this.loadBuyer(product, userStore, username, token).then((buyer?: Buyer) => {
                        setData({
                            ...fetchData,
                            category,
                            product,
                            buyer,
                            forceUpdate: props.forceUpdate,
                        });
                    });
                });
        });
    };

    createFetchData = (props: FetchProps) => {
        const { api, userStore, site } = this.context;

        const superCategoryStore = new SuperCategoryStore({ api, userStore });
        const categoryStore = new CategoryStore({ api, userStore });
        const ruleStore = new RuleStore({ api });
        const reviewStore = new ReviewStore({ api, userStore });
        const searchApi = new SearchApi({ api });

        return {
            superCategoryStore,
            categoryStore,
            reviewStore,
            ruleStore,
            userStore,
            site,
            searchApi,
            ...props,
        };
    };

    transform: Transform<Data, Data, FetchProps> = (data, props) => {
        const fetchData = this.createFetchData(props);
        const { categoryStore } = fetchData;

        const category = categoryStore.processJSON({ json: data.category });
        const product = category.productStore.processJSON({ json: data.product });
        const buyer = product.buyerStore.processJSON({ json: data.buyer });

        return { ...fetchData, forceUpdate: props.forceUpdate, category, product, buyer };
    };

    renderFetch: Render<Data> = ({ loading, error, data }) => (
        <LoadingProgress loading={loading}>
            <ErrorPage error={error}>
                {data ? (
                    <ProductView
                        product={data.product}
                        productBuyer={data.buyer}
                        category={data.category}
                        productStore={data.category.productStore}
                        userStore={data.userStore}
                        superCategoryStore={data.superCategoryStore}
                        forceUpdate={data.forceUpdate}
                        searchApi={data.searchApi}
                    >
                        <ProductNegotiationView
                            product={data.product}
                            buyer={data.buyer}
                            token={data.token}
                            userStore={data.userStore}
                            isAuthor={this.isAuthor(data.product, data.userStore)}
                            reviewStore={data.reviewStore}
                            forceUpdate={data.forceUpdate}
                        />
                    </ProductView>
                ) : null}
            </ErrorPage>
        </LoadingProgress>
    );

    render() {
        return (
            <ForceUpdate>
                {({ forceUpdate }) => (
                    <ViewerConsumer>
                        {viewer => (
                            <FetchData
                                serializerKey={__filename}
                                fetch={this.fetch}
                                render={this.renderFetch}
                                transform={this.transform}
                                forceUpdate={forceUpdate}
                                categorySlug={this.props.match.params.categorySlug}
                                differentiator={this.props.match.params.differentiator}
                                slug={this.props.match.params.slug}
                                username={this.props.search.username}
                                token={this.props.search.token}
                                viewer={viewer}
                            />
                        )}
                    </ViewerConsumer>
                )}
            </ForceUpdate>
        );
    }
}

export default ProductLoader;
